import * as React from 'react';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/variables';

import { CURRENT_CART_VERSION } from './constants';
import { ITEM_ADDED_TO_CART, ITEM_REMOVED_FROM_CART, ITEM_UPDATED_IN_CART } from './events';
import { ICartItems, ICurrency } from './types';
import {
  formatCartItems, countCartItems, retrieveCartItems, removeCartItems, migrateCartItems, returnTotalAmount
} from './helpers';
import t from './locales';
import Button from '../Shared/Button';
import InventoryIconView from './InventoryIconView';
import InventoryListView from './InventoryListView';
import InventoryOrderItemList from './InventoryOrderItemList';
import InventoryYadoOrderItemList from './InventoryYadoOrderItemList';
import JRWInventoryTicketOrderItemList from "./JRWInventoryTicketOrderItemList";
import TWPInventoryTicketOrderItemList from "./TWPInventoryTicketOrderItemList";
import JEPInventoryTicketOrderItemList from "./JEPInventoryTicketOrderItemList";
import InventoryFooterView from './InventoryFooterView';
import Price from '../Shared/Price';

const InventoryWrapperView = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
`;

const InventoryView = styled.div`
  background-color: ${colors.white};
  position: fixed;
  width: 100%;
  max-width: 400px;
  top: 50px;
  right: 0;
  z-index: 10;
  box-shadow: 1px 3px 10px 2px rgba(66, 66, 66, 0.25);
  border-radius: 6px;
  @media screen and (max-width: 575.98px) {
    position: absolute
    width: 100vw;
    max-width: 400px;
    top: 47px;
    right: -54px;
  }
`;

const InventoryEmptyView = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 14px;
  color: ${colors.gray600};
`;


interface IInventoryPropsFromServerSide {
  cartVersion?: string,
  cartItems: ICartItems,
  currentExchangeRate?: number,
  localCurrency?: ICurrency
}

const Inventory: React.FC<IInventoryPropsFromServerSide> = (props) => {
  const [cartItems, setCartItems] = useState<ICartItems>(formatCartItems({}));
  const [isActive, setActive] = useState(false);
  const currentExchangeRate = props.currentExchangeRate;
  const localCurrency = props.localCurrency;

  function updateCartItems(event: CustomEvent): void {
    setCartItems(event.detail.cartItems);
  }

  function showInventory(event: React.MouseEvent): void {
    event.preventDefault();
    setActive(!isActive);
  }

  function hideInventory(event: Event): void {
    const container = document.querySelector("*[class^=InventoryWrapperView]");
    if (container && container instanceof Node && !(container.contains(event.target as Node))) {
      setActive(false);
    }
  }

  useEffect(() => {
    migrateCartItems();
    const isShowPaymentResultPath = location.pathname.match(/^\/snow\/me\/payment\/\d{1,}$/);
    const hasDeleteCartParam = location.search.match(/delete_cart=true/);
    if (isShowPaymentResultPath && hasDeleteCartParam) {
      removeCartItems(CURRENT_CART_VERSION);
    }

    const cartItemsFromServerSide = formatCartItems(props.cartItems);
    if (countCartItems(cartItemsFromServerSide) >= 0 && props.cartVersion === CURRENT_CART_VERSION) {
      // サーバーのカートバージョンが正しければサーバーを優先
      setCartItems(cartItemsFromServerSide);
    } else {
      setCartItems(retrieveCartItems(CURRENT_CART_VERSION));
    }

    document.addEventListener(ITEM_ADDED_TO_CART, updateCartItems);
    document.addEventListener(ITEM_REMOVED_FROM_CART, updateCartItems);
    document.addEventListener(ITEM_UPDATED_IN_CART, updateCartItems);
    document.addEventListener('click', hideInventory);
    return () => {
      document.removeEventListener(ITEM_ADDED_TO_CART, updateCartItems);
      document.removeEventListener(ITEM_REMOVED_FROM_CART, updateCartItems);
      document.removeEventListener(ITEM_UPDATED_IN_CART, updateCartItems);
      document.removeEventListener('click', hideInventory);
    }
  }, []);

  const count = countCartItems(cartItems);
  return (
    <InventoryWrapperView>
      <InventoryIconView href="#" onClick={showInventory} className="menuBtn">
        <i className="menuBtnIcon icon-cart" />
        {count > 0 && <span className="badge">{count}</span>}
      </InventoryIconView>
      {isActive &&
        <InventoryView>
          {count > 0 ?
            <React.Fragment>
              <InventoryListView>
                <InventoryYadoOrderItemList
                  items={cartItems.yadoOrderItems}
                  currentExchangeRate={currentExchangeRate}
                  localCurrency={localCurrency} />
                <InventoryOrderItemList
                  items={cartItems.orderItems}
                  currentExchangeRate={currentExchangeRate}
                  localCurrency={localCurrency} />
                <InventoryOrderItemList
                  items={cartItems.insuranceOrderItems}
                  currentExchangeRate={currentExchangeRate}
                  localCurrency={localCurrency} />
                <JRWInventoryTicketOrderItemList
                  items={cartItems.jrwTicketOrderItems}
                  currentExchangeRate={currentExchangeRate}
                  localCurrency={localCurrency} />
                <TWPInventoryTicketOrderItemList
                  items={cartItems.twpTicketOrderItems}
                  currentExchangeRate={currentExchangeRate}
                  localCurrency={localCurrency} />
                <JEPInventoryTicketOrderItemList
                  items={cartItems.jepTicketOrderItems}
                  currentExchangeRate={currentExchangeRate}
                  localCurrency={localCurrency} />
              </InventoryListView>
              <InventoryFooter cartItems={cartItems} currentExchangeRate={currentExchangeRate} localCurrency={localCurrency} />
            </React.Fragment> :
          <InventoryEmptyView>
            {t.cartIsEmpty()}
          </InventoryEmptyView>}
        </InventoryView>
      }
    </InventoryWrapperView>
  );
}

interface IInventoryFooterProps {
  cartItems: ICartItems, 
  currentExchangeRate?: number,
  localCurrency?: ICurrency
}

const InventoryFooter: React.FC<IInventoryFooterProps> = (props) => {
  const [isLoading, setLoading] = useState(false);
  const totalPrice = returnTotalAmount(props.cartItems);
  const currentExchangeRate = props.currentExchangeRate;
  const localCurrency = props.localCurrency;
  return (
    <InventoryFooterView>
      <Price
        price={totalPrice.price}
        fullPrice={totalPrice.fullPrice}
        style={{ marginRight: 8 }}
        showDiscount
        localCurrency={localCurrency}
      />
      <Button primary href="/snow/me/shopping_cart" isLoading={isLoading} onClick={() => { setLoading(true); }}>
        {t.seeCart()}
      </Button>
    </InventoryFooterView>
  );
}


export default Inventory;
